import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Chip, CircularProgress, Alert, Avatar, Button, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, useTheme, useMediaQuery,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api.service';
import { commissionService, RECIPIENT_TYPES } from '../services/accounting/commission.service';
import { can } from '../utils/roleUtils';
import UserDialog from '../components/team/UserDialog';
import Roles from './settings/Roles';
import { ProTableStats, useColumnPreferences } from '../components/pro-table';
import ProTablePreferences from '../components/pro-table/ProTablePreferences';
import { getBrandedTableHeadSx } from '../utils/colorUtils';
import { staffService, EMPLOYMENT_TYPES } from '../services/accounting/staff.service';
import { employmentTypeOf, isLoginEnabled, salaryLabel, matchesTypeFilter, displayUsername } from './team/teamMembers';
import { saveMember } from '../components/team/memberForm';
import { partnerPeopleService, linksFrom } from '../services/accounting/partnerPeople.service';
import profitShareService from '../services/accounting/profitShare.service';
import { ownerOptions } from './team/teamMembers';
import taskService from '../services/task.service';

const f = { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' };
const money = (v) => `AED ${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Red is kept for problems, so roles use neutral colours.
const ROLE_LABELS = {
  TC_ADMIN: { label: 'Admin', color: 'primary', short: 'A' },
  TC_STAFF: { label: 'Staff', color: 'default', short: 'ST' },
  PLATFORM_ADMIN: { label: 'Platform Admin', color: 'secondary', short: 'PA' },
  PLATFORM_SUPPORT: { label: 'Support', color: 'default', short: 'S' },
};

const roleOf = (user) => {
  const base = ROLE_LABELS[user.role] || { label: user.role, color: 'default', short: '?' };
  const staffName = user.role === 'TC_STAFF' ? (user.role_name || user.roleName) : null;
  return staffName ? { ...base, label: staffName } : base;
};
const nameOf = (user) => user.fullName || user.full_name || user.username;
const avatarBg = (role) => (role.color === 'default' ? 'grey.500' : `${role.color}.main`);
const isActive = (user) => user.active !== false;

const HEAD_SX = { fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' };
const CELL_SX = { fontSize: '0.8125rem', py: 0.75, whiteSpace: 'nowrap' };
const CHIP_SX = { fontSize: '0.65rem', height: 20 };
const VIEW_KEY = 'teamView';
const initialsOf = (user) => (nameOf(user) || '?').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

// ─── Owners ──────────────────────────────────────────────────────────────────

// The partners of this typing center and the person each one is; with none set up, a TC admin can set up the owner (100%).
const OwnersSection = ({ partners, users, businessType, canManage, onRecordDrawing, onSetUp }) => {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const active = partners.filter((p) => p.active !== false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSetUp(userId);
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Could not set up the owner.');
    } finally { setBusy(false); }
  };

  if (active.length === 0) {
    if (!canManage) return null;
    return (
      <>
        <Alert severity="info" sx={{ mb: 1.5, fontSize: f.xs }}
          action={<Button color="inherit" size="small" onClick={() => { setUserId(''); setError(null); setOpen(true); }}>Set up owner (100%)</Button>}>
          No owner is set up yet. Set up the owner so drawings and the profit share can be recorded.
        </Alert>
        <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: f.md }}>Set up owner (100%)</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: f.xs }}>{error}</Alert>}
            <TextField select fullWidth size="small" label="Team member" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ mt: 1 }}>
              {users.filter((u) => u.active !== false).map((u) => <MenuItem key={u.id} value={u.id}>{nameOf(u)}</MenuItem>)}
            </TextField>
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mt: 1 }}>
              Creates one active partner at 100% named after this person and links them.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={busy || !userId} sx={{ textTransform: 'none' }}>{busy ? 'Saving...' : 'Set up owner'}</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: f.sm, fontWeight: 600, px: 1.5, pt: 1, pb: 0.5 }}>Owners</Typography>
      {active.map((p) => (
        <Box key={p.id} display="flex" alignItems="center" gap={1.5} sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: f.sm, fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
            {p.name}
            <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1, fontWeight: 400 }}>
              {p.person ? `${p.person.fullName} · login ${p.person.loginEnabled ? '✓' : '—'}` : 'Not linked to a team member'}
            </Typography>
          </Typography>
          <Chip label="Owner" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          <Typography sx={{ fontSize: f.sm, fontWeight: 700, width: 52, textAlign: 'right' }}>{Number(p.ownershipPercent)}%</Typography>
          {businessType === 'SOLE_ESTABLISHMENT' && (
            <Button size="small" onClick={() => onRecordDrawing(p.id)} sx={{ fontSize: f.xs, textTransform: 'none' }}>Record drawing</Button>
          )}
        </Box>
      ))}
    </Paper>
  );
};

// ─── Member detail ───────────────────────────────────────────────────────────

// Salary history of one staff profile, newest first (finance read).
const SalaryHistory = ({ profileId }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    staffService.salaryHistory(profileId)
      .then((list) => { if (!cancelled) setRows(list || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.message || 'Could not load the salary history.'); });
    return () => { cancelled = true; };
  }, [profileId]);

  if (error) return <Alert severity="error" sx={{ fontSize: f.xs }}>{error}</Alert>;
  if (rows === null) return <CircularProgress size={18} />;
  if (rows.length === 0) return <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>No salary set.</Typography>;
  return (
    <Box>
      {rows.map((r) => (
        <Box key={r.id} display="flex" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: f.sm }}>
            From {new Date(`${r.effectiveFrom}T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
            {(r.changedByName || r.createdAt) && (
              <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1 }}>
                Set{r.changedByName ? ` by ${r.changedByName}` : ''}{r.createdAt ? ` · ${new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              </Typography>
            )}
          </Typography>
          <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>
            {money(r.total)}
            {r.basic != null && (
              <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1 }}>
                basic {money(r.basic)} + allowances {money(r.allowances || 0)}
              </Typography>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 110 }}>
    <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: f.sm, mt: 0.25 }}>{children}</Box>
  </Box>
);

const maskTail = (v, keep = 4) => (v ? `•••• ${String(v).replace(/\s+/g, '').slice(-keep)}` : '—');

const fmtDate = (v) => (v ? new Date(String(v).length === 10 ? `${v}T00:00:00` : v)
  .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// Open tasks assigned to one member (real /api/tasks?assignedTo=).
const MemberWork = ({ userId, onOpenTasks }) => {
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    taskService.getAll({ assignedTo: userId })
      .then((r) => { if (!cancelled) setTasks(r.data || []); })
      .catch(() => { if (!cancelled) setError('Could not load tasks.'); });
    return () => { cancelled = true; };
  }, [userId]);

  if (error) return <Alert severity="error" sx={{ fontSize: f.xs }}>{error}</Alert>;
  if (tasks === null) return <CircularProgress size={18} />;
  const open = tasks.filter((t) => t.status !== 'COMPLETED');
  const done = tasks.length - open.length;
  return (
    <Box>
      <Box display="flex" gap={3} mb={1.5}>
        <Field label="Open tasks"><Typography sx={{ fontSize: f.md, fontWeight: 600 }}>{open.length}</Typography></Field>
        <Field label="Completed"><Typography sx={{ fontSize: f.md, fontWeight: 600 }}>{done}</Typography></Field>
      </Box>
      {open.length === 0
        ? <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>No open tasks.</Typography>
        : open.slice(0, 6).map((t) => (
          <Box key={t.id} display="flex" justifyContent="space-between" gap={1}
            sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography noWrap sx={{ fontSize: f.sm, minWidth: 0 }}>
              {t.taskName}
              {t.workOrderNumber && <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1 }}>{t.workOrderNumber}</Typography>}
            </Typography>
            <Chip label={t.status === 'IN_PROGRESS' ? 'In progress' : 'Pending'} size="small"
              color={t.status === 'IN_PROGRESS' ? 'info' : 'default'} sx={CHIP_SX} />
          </Box>
        ))}
      <Button size="small" onClick={onOpenTasks} sx={{ mt: 1, fontSize: f.xs, textTransform: 'none' }}>Open Tasks</Button>
    </Box>
  );
};

// Member detail: one popup, tabs only for what applies to this person.
const DetailDialog = ({
  open, onClose, user, recipient, profile, bankDetails, partner, canSeeSalary, canEditSalary, onOpenCommissions, onOpenDrawings, onOpenTasks, onEdit,
}) => {
  const [tab, setTab] = useState('overview');
  useEffect(() => { if (open) setTab('overview'); }, [open, user?.id]);
  if (!user) return null;

  const role = roleOf(user);
  const type = employmentTypeOf(user, profile, partner);
  const login = isLoginEnabled(user);
  const owed = Number(recipient?.receivable || 0);
  const current = profile?.currentSalary;
  const bank = bankDetails || {};

  const tabs = [
    { key: 'overview', label: 'Overview' },
    canSeeSalary && (type === 'EMPLOYEE' || (type === 'OWNER' && profile)) && { key: 'salary', label: 'Salary' },
    canSeeSalary && recipient && { key: 'commission', label: 'Commission' },
    (type === 'OWNER' || partner) && { key: 'ownership', label: 'Ownership' },
    login && { key: 'work', label: 'Work' },
  ].filter(Boolean);

  const stat = (label, value, color) => (
    <Grid item xs={6} sm={3}>
      <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center' }}>
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ fontSize: f.md, fontWeight: 600, color }}>{money(value)}</Typography>
      </Paper>
    </Grid>
  );

  const overview = (
    <Box>
      <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
        <Field label="Type">{EMPLOYMENT_TYPES[type]}</Field>
        <Field label="Role">
          <Chip label={role.label} size="small" color={role.color} sx={CHIP_SX} />
          {user.role === 'TC_ADMIN' && <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1 }}>Full access</Typography>}
        </Field>
        <Field label="Status">
          <Chip label={isActive(user) ? 'Active' : 'Inactive'} size="small" color={isActive(user) ? 'success' : 'default'} sx={CHIP_SX} />
        </Field>
        <Field label="Joined">{fmtDate(profile?.joinDate)}</Field>
      </Box>
      <Box display="flex" gap={3} flexWrap="wrap">
        <Field label="Sign-in">{login ? user.username : 'No login · no seat'}</Field>
        <Field label="Email">{login ? (user.email || '—') : '—'}</Field>
        {canSeeSalary && type !== 'EXTERNAL' && (
          <Field label="Salary">{current?.total != null ? money(current.total) : '—'}</Field>
        )}
        {canSeeSalary && recipient && (
          <Field label="Commission payable">{money(recipient.payable)}</Field>
        )}
      </Box>
      {canSeeSalary && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
          <Box display="flex" alignItems="center" gap={0.75} mb={1}>
            <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>Bank details</Typography>
            <Box flex={1} />
            {onEdit && (
              <Button size="small" onClick={onEdit} sx={{ fontSize: f.xs, textTransform: 'none' }}>
                {bank.bankName || bank.iban || bank.accountNumber ? 'Edit' : 'Add bank details'}
              </Button>
            )}
          </Box>
          {bank.bankName || bank.iban || bank.accountNumber ? (
            <Box display="flex" gap={3} flexWrap="wrap">
              <Field label="Bank">{bank.bankName || '—'}</Field>
              <Field label="IBAN">{maskTail(bank.iban)}</Field>
              <Field label="Account number">{maskTail(bank.accountNumber)}</Field>
            </Box>
          ) : (
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>
              No bank details yet — used for salary (WPS) and commission payouts.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );

  const salary = (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1.5}>
        <Box>
          <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>Current monthly salary</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>{current?.total != null ? money(current.total) : 'Not set'}</Typography>
          {current?.basic != null && (
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>
              basic {money(current.basic)} + allowances {money(current.allowances || 0)}
            </Typography>
          )}
        </Box>
        {canEditSalary && <Button size="small" onClick={onEdit} sx={{ fontSize: f.xs, textTransform: 'none' }}>Change salary</Button>}
      </Box>
      <Typography sx={{ fontSize: f.xs, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>History</Typography>
      {profile ? <SalaryHistory profileId={profile.id} /> : <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>No salary set.</Typography>}
    </Box>
  );

  const commission = recipient && (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography sx={{ fontSize: f.sm, color: 'text.secondary' }}>
          {RECIPIENT_TYPES[recipient.type] || recipient.type}{recipient.active === false ? ' (inactive)' : ''}
        </Typography>
        <Button size="small" onClick={onOpenCommissions} sx={{ fontSize: f.xs, textTransform: 'none' }}>Open in Commissions</Button>
      </Box>
      <Grid container spacing={1.5}>
        {stat('Earned', recipient.earned, 'success.main')}
        {stat('Paid', recipient.paid)}
        {stat('Payable', recipient.payable, 'warning.main')}
        {stat('Owes back', owed, owed > 0 ? 'error.main' : 'text.secondary')}
      </Grid>
    </Box>
  );

  const ownership = (
    <Box>
      {partner ? (
        <>
          <Box display="flex" gap={3} flexWrap="wrap" mb={1.5}>
            <Field label="Partner">{partner.name}</Field>
            <Field label="Ownership"><Typography sx={{ fontSize: f.md, fontWeight: 600 }}>{Number(partner.ownershipPercent)}%</Typography></Field>
            <Field label="Status">
              <Chip label={partner.active !== false ? 'Active' : 'Inactive'} size="small"
                color={partner.active !== false ? 'success' : 'default'} sx={CHIP_SX} />
            </Field>
          </Box>
          <Button size="small" onClick={() => onOpenDrawings(partner.id)} sx={{ fontSize: f.xs, textTransform: 'none' }}>
            Open Owner Drawings
          </Button>
        </>
      ) : (
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>Not linked to a partner yet — edit the member to pick one.</Typography>
      )}
    </Box>
  );

  const body = { overview, salary, commission, ownership, work: <MemberWork userId={user.id} onOpenTasks={onOpenTasks} /> };
  const active = tabs.some((t) => t.key === tab) ? tab : 'overview';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { minHeight: 380 } }}>
      <DialogTitle sx={{ pb: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ width: 40, height: 40, fontSize: f.md, bgcolor: avatarBg(role) }}>{initialsOf(user)}</Avatar>
            <Box>
              <Typography component="span" sx={{ fontSize: f.md, fontWeight: 600, display: 'block' }}>{nameOf(user)}</Typography>
              <Box display="flex" gap={0.5} mt={0.25}>
                <Chip label={EMPLOYMENT_TYPES[type]} size="small" variant="outlined" sx={CHIP_SX} />
                <Chip label={role.label} size="small" color={role.color} sx={CHIP_SX} />
                {!login && <Chip label="No login" size="small" sx={CHIP_SX} />}
              </Box>
            </Box>
          </Box>
          <Box>
            {onEdit && <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>}
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        {tabs.length > 1 && (
          <Tabs value={active} onChange={(e, v) => setTab(v)} variant="scrollable"
            sx={{ mt: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: f.sm, textTransform: 'none', px: 1.5 } }}>
            {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {body[active]}
      </DialogContent>
    </Dialog>
  );
};

const UserCard = ({ user, recipient, onClick, onEdit }) => {
  const role = roleOf(user);
  return (
    <Card variant="outlined" sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }} onClick={onClick}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
          <Avatar sx={{ width: 36, height: 36, fontSize: f.sm, bgcolor: avatarBg(role) }}>{initialsOf(user)}</Avatar>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontSize: f.sm, fontWeight: 600, lineHeight: 1.2 }} noWrap>{nameOf(user)}</Typography>
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }} noWrap>{displayUsername(user)}</Typography>
          </Box>
          {!isLoginEnabled(user) && <Chip label="No login" size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />}
          <Chip label={role.label} size="small" color={role.color} sx={{ fontSize: '0.65rem', height: 20 }} />
          {onEdit && (
            <Tooltip title="Edit member">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(user); }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <EmailIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }} noWrap>{user.email}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
          <Box display="flex" gap={0.5}>
            <Chip label={user.active !== false ? 'Active' : 'Inactive'} size="small"
              color={user.active !== false ? 'success' : 'default'} sx={{ fontSize: '0.6rem', height: 18 }} />
            {recipient && recipient.active !== false && (
              <Chip label="Agent" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
            )}
            {Number(recipient?.receivable) > 0 && (
              <Chip label="Owes back" size="small" color="error" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
            )}
          </Box>
          <Typography sx={{ fontSize: f.xs, color: 'primary.main' }}>View details →</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const Team = ({ currentUser }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
  const canCommission = can(currentUser, 'finance', 'write');
  const canSeeCommission = can(currentUser, 'finance', 'read');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageTab = searchParams.get('tab') === 'roles' ? 'roles' : 'members';
  const changePageTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'roles') params.set('tab', 'roles'); else params.delete('tab');
    setSearchParams(params, { replace: true });
  };
  const [statFilter, setStatFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerLinks, setPartnerLinks] = useState([]);
  const [businessType, setBusinessType] = useState('SOLE_ESTABLISHMENT');
  const [search, setSearch] = useState('');
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'table'; } catch { return 'table'; }
  });
  const changeView = (next) => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* storage unavailable */ }
  };
  const columns = useMemo(() => [
    { key: 'name', label: 'Name' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'login', label: 'Login' },
    { key: 'type', label: 'Type' },
    { key: 'role', label: 'Role' },
    ...(canSeeCommission ? [{ key: 'salary', label: 'Salary' }] : []),
    { key: 'status', label: 'Status' },
    ...(canSeeCommission ? [{ key: 'commission', label: 'Commission' }] : []),
  ], [canSeeCommission]);
  const colPrefs = useColumnPreferences('team', columns);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailBank, setDetailBank] = useState(null);
  const [editBank, setEditBank] = useState(null);
  const [grantAccess, setGrantAccess] = useState(false);

  // The person's bank details (finance read); the Team list does not need them.
  const fetchBank = useCallback(async (u) => {
    if (!u || !canSeeCommission) return null;
    try { return await staffService.getBank(u.id); } catch { return null; }
  }, [canSeeCommission]);

  useEffect(() => {
    let alive = true;
    setDetailBank(null);
    fetchBank(detailUser).then((b) => { if (alive) setDetailBank(b); });
    return () => { alive = false; };
  }, [detailUser, fetchBank]);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [usersResp, rolesResp, recipientList, profileList, partnerResp, settingsResp] = await Promise.all([
        ApiService.get(`/api/users/typing-center/${typingCenterId}`),
        ApiService.getRoles({ includeArchived: false }),
        canSeeCommission ? commissionService.getRecipients().catch(() => []) : Promise.resolve([]),
        staffService.list().catch(() => []),
        profitShareService.getPartners().catch(() => null),
        ApiService.get('/api/settings/accounting').catch(() => null),
      ]);
      setUsers(usersResp?.data?.data || []);
      setRoles(rolesResp?.data?.data || []);
      setRecipients(recipientList);
      setProfiles(profileList || []);
      setPartners(partnerResp?.data || []);
      setPartnerLinks(linksFrom(partnerResp?.data || []));
      setBusinessType(settingsResp?.data?.data?.business_type === 'COMPANY' ? 'COMPANY' : 'SOLE_ESTABLISHMENT');
    } catch (err) {
      setError('Failed to load team data');
    } finally { setLoading(false); }
  }, [typingCenterId, canSeeCommission]);

  useEffect(() => { if (typingCenterId) loadData(); }, [typingCenterId, loadData]);

  // Roles tab edits: refresh only the role list (used by Add / Edit Member) without the full-page loader.
  const refreshRoles = useCallback(async () => {
    try {
      const rolesResp = await ApiService.getRoles({ includeArchived: false });
      setRoles(rolesResp?.data?.data || []);
    } catch { /* the Roles tab shows its own errors */ }
  }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); }
    return undefined;
  }, [success]);

  const recipientOf = (u) => recipients.find((r) => r.userId === u?.id) || null;
  const profileOf = (u) => profiles.find((p) => p.userId === u?.id) || null;
  const partnerOf = (u) => {
    const link = partnerLinks.find((l) => l.userId === u?.id);
    return link ? partners.find((p) => p.id === link.partnerId) || null : null;
  };
  const isAgent = (u) => { const r = recipientOf(u); return Boolean(r && r.active !== false); };

  // Member create / edit, then its commission profile. The dialog shows API errors inline, so let them propagate.
  // Member create / edit, then staff profile, salary and commission profile. The dialog shows errors inline.
  // After a partial save the dialog switches to the saved member, so a retry is an update, never a second person.
  const handleSaveUser = async (payload, commission, staff, owner) => {
    setUserSaving(true);
    try {
      const { savedUser, error: saveError } = await saveMember({
        api: ApiService, staffService, commissionService, partnerPeopleService, editingUser, typingCenterId, payload,
        staffChanges: staff, owner, commission, recipient: recipientOf(editingUser), canEditSalary: canCommission,
      });
      if (savedUser) loadData();
      if (saveError) {
        if (savedUser) setEditingUser(savedUser);
        throw new Error(saveError);
      }
      setSuccess(editingUser ? 'Member updated' : 'Member added');
      setUserDialogOpen(false);
      setEditingUser(null);
    } finally { setUserSaving(false); }
  };

  // A link with ?edit=<userId>&access=1 (Partners → Give access) opens that member with sign-in on.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam || loading) return;
    const target = users.find((u) => u.id === editParam);
    const params = new URLSearchParams(searchParams);
    params.delete('edit');
    params.delete('access');
    setSearchParams(params, { replace: true });
    if (target) openEdit(target, searchParams.get('access') === '1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, loading]);

  const handleSetUpOwner = async (userId) => {
    await partnerPeopleService.setupOwner(userId);
    setSuccess('Owner set up');
    loadData();
  };

  const agentCount = users.filter(isAgent).length;
  const activeCount = users.filter(isActive).length;
  const noLoginCount = users.filter((u) => !isLoginEnabled(u)).length;
  const roleOptions = [...new Set(users.map((u) => roleOf(u).label))].sort();
  const term = search.trim().toLowerCase();
  const shown = users.filter((u) => {
    if (statFilter === 'STAFF' && isAgent(u)) return false;
    if (statFilter === 'AGENTS' && !isAgent(u)) return false;
    if (statFilter === 'ACTIVE' && !isActive(u)) return false;
    if (statFilter === 'NO_LOGIN' && isLoginEnabled(u)) return false;
    if (roleFilter && roleOf(u).label !== roleFilter) return false;
    if (!matchesTypeFilter(u, profileOf(u), typeFilter, partnerOf(u))) return false;
    if (term && ![nameOf(u), displayUsername(u), u.email].some((v) => (v || '').toLowerCase().includes(term))) return false;
    return true;
  });

  const openEdit = async (u, access = false) => { setEditBank(await fetchBank(u)); setGrantAccess(access === true); setEditingUser(u); setUserDialogOpen(true); };

  const renderCell = (col, user) => {
    const role = roleOf(user);
    const recipient = recipientOf(user);
    const profile = profileOf(user);
    switch (col.key) {
      case 'name':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: avatarBg(role) }}>{initialsOf(user)}</Avatar>
            <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500 }} noWrap>{nameOf(user)}</Typography>
          </Box>
        );
      case 'login':
        return isLoginEnabled(user)
          ? <Typography component="span" sx={{ fontSize: '0.8125rem', color: 'success.main' }}>✓</Typography>
          : <Tooltip title="Cannot sign in — no seat used"><Typography component="span" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>—</Typography></Tooltip>;
      case 'type':
        return <Chip label={EMPLOYMENT_TYPES[employmentTypeOf(user, profile, partnerOf(user))]} size="small" variant="outlined" sx={CHIP_SX} />;
      case 'salary':
        return salaryLabel(profile, canSeeCommission);
      case 'username': return displayUsername(user);
      case 'email': return user.email || '—';
      case 'role': return <Chip label={role.label} size="small" color={role.color} sx={CHIP_SX} />;
      case 'status':
        return <Chip label={isActive(user) ? 'Active' : 'Inactive'} size="small" color={isActive(user) ? 'success' : 'default'} sx={CHIP_SX} />;
      case 'commission':
        if (!recipient || recipient.active === false) {
          return <Typography component="span" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>—</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip label="Agent" size="small" color="secondary" variant="outlined" sx={CHIP_SX} />
            {Number(recipient.receivable) > 0 && <Chip label="Owes back" size="small" color="error" variant="outlined" sx={CHIP_SX} />}
          </Box>
        );
      default: return null;
    }
  };

  const renderMembers = () => (
    <>
      <ProTableStats
        cards={[
          { key: 'ALL', label: 'All members', value: users.length },
          { key: 'STAFF', label: 'Staff', value: users.length - agentCount },
          ...(canSeeCommission ? [{ key: 'AGENTS', label: 'Agents', value: agentCount, color: 'secondary.main' }] : []),
          { key: 'ACTIVE', label: 'Active', value: activeCount, color: 'success.main' },
          { key: 'NO_LOGIN', label: 'No login', value: noLoginCount },
        ].map((c) => ({ ...c, highlight: statFilter === c.key, onClick: () => setStatFilter(c.key) }))}
      />

      {canSeeCommission && (
        <OwnersSection partners={partners} users={users} businessType={businessType} canManage={currentUser?.role === 'TC_ADMIN'}
          onRecordDrawing={(partnerId) => navigate(`/accounting/drawings?partnerId=${partnerId}`)} onSetUp={handleSetUpOwner} />
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField
          placeholder="Search by name, username or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 } }}>
          <InputLabel sx={{ fontSize: '0.8125rem' }}>Role</InputLabel>
          <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)} sx={{ fontSize: '0.8125rem' }}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All roles</MenuItem>
            {roleOptions.map((r) => <MenuItem key={r} value={r} sx={{ fontSize: '0.8125rem' }}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 } }}>
          <InputLabel sx={{ fontSize: '0.8125rem' }}>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)} sx={{ fontSize: '0.8125rem' }}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All types</MenuItem>
            {Object.entries(EMPLOYMENT_TYPES).map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: '0.8125rem' }}>{l}</MenuItem>)}
            <MenuItem value="NO_LOGIN" sx={{ fontSize: '0.8125rem' }}>No login</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {shown.length} member{shown.length === 1 ? '' : 's'}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && changeView(v)} sx={{ height: 32 }}>
            <ToggleButton value="table" aria-label="Table view"><Tooltip title="Table view"><TableViewIcon fontSize="small" /></Tooltip></ToggleButton>
            <ToggleButton value="cards" aria-label="Card view"><Tooltip title="Card view"><CardViewIcon fontSize="small" /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {view === 'table' && <ProTablePreferences columns={columns} preferences={colPrefs} />}
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />}
            onClick={() => { setEditBank(null); setGrantAccess(false); setEditingUser(null); setUserDialogOpen(true); }}
            sx={{ textTransform: 'none', fontSize: '0.8125rem', height: 32, whiteSpace: 'nowrap', ml: 0.5 }}>
            Add Member
          </Button>
        </Box>
      </Box>

      {shown.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
          <PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            {users.length === 0 ? 'No team members yet' : 'No members match these filters'}
          </Typography>
        </Paper>
      ) : view === 'table' && !isMobile ? (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  {colPrefs.visibleColumns.map((col) => <TableCell key={col.key} sx={HEAD_SX}>{col.label}</TableCell>)}
                  <TableCell align="right" sx={HEAD_SX}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((user) => (
                  <TableRow key={user.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailUser(user)}>
                    {colPrefs.visibleColumns.map((col) => (
                      <TableCell key={col.key} sx={CELL_SX}>{renderCell(col, user)}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ ...CELL_SX, py: 0.25 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setDetailUser(user)} sx={{ color: 'text.secondary' }}>
                          <ViewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit member">
                        <IconButton size="small" onClick={() => openEdit(user)} sx={{ color: 'text.secondary' }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {shown.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <UserCard user={user} recipient={recipientOf(user)} onClick={() => setDetailUser(user)} onEdit={openEdit} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs value={pageTab} onChange={(_, v) => changePageTab(v)} sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="members" label={`Members (${users.length})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 }} />
        <Tab value="roles" label="Roles" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 }} />
      </Tabs>

      {pageTab === 'roles'
        ? <Roles currentUser={currentUser} embedded onRolesChanged={refreshRoles} />
        : renderMembers()}

      <UserDialog open={userDialogOpen} onClose={() => { setUserDialogOpen(false); setEditingUser(null); }}
        onSave={handleSaveUser} user={editingUser} roles={roles} saving={userSaving}
        recipient={recipientOf(editingUser)} canCommission={canCommission}
        profile={profileOf(editingUser)} bank={editBank} canEditSalary={canCommission} grantAccess={grantAccess}
        ownerOpts={ownerOptions(partners, partnerLinks, editingUser?.id)} businessType={businessType} />

      <DetailDialog open={Boolean(detailUser)} onClose={() => setDetailUser(null)} user={detailUser} recipient={recipientOf(detailUser)}
        profile={profileOf(detailUser)} bankDetails={detailBank} partner={partnerOf(detailUser)} canSeeSalary={canSeeCommission}
        onOpenCommissions={() => navigate('/accounting/commissions/recipients')}
        onOpenDrawings={(partnerId) => navigate(`/accounting/drawings?partnerId=${partnerId}`)}
        onOpenTasks={() => navigate('/tasks')}
        canEditSalary={canCommission}
        onEdit={() => { const u = detailUser; setDetailUser(null); openEdit(u); }} />
    </Box>
  );
};

export default Team;
